"use strict";

export class Vector2 {
    static zero() {
        return { x: 0, y: 0 };
    }
    static up() {
        return { x: 0, y: -1 };
    }
    static right() {
        return { x: 1, y: 0 };
    }
    static down() {
        return { x: 0, y: 1 };
    }
    static left() {
        return { x: -1, y: 0 };
    }

    static add(v1,v2) {
        return {
            x: v1.x + v2.x,
            y: v1.y + v2.y
        };
    }

    static subtract(vector,fromVector) {
        return {
            x: fromVector.x - vector.x,
            y: fromVector.y - vector.y
        };
    }

    static multiply(vector,value) {
        return { 
            x: vector.x * value,
            y: vector.y * value
        };
    }
    static multiplySize(size,value) {
        return {
            width: size.width * value,
            height: size.height * value
        };
    }

    static divide(vector,value) {
        return {
            x: vector.x / value,
            y: vector.y / value
        };
    }
    static divideSize(size,value) {
        return {
            width: size.width / value,
            height: size.height / value
        };
    }

    static __magnitude(x,y) {
        return Math.sqrt(x * x + y * y);
    }
    static magnitude(v1,v2) {
        return v2 === undefined ? 
            Vector2.__magnitude(v1.x, v1.y) :
            Vector2.__magnitude(v1.x - v2.x, v1.y - v2.y);
    }

    static normalize(vector) {
        let mag = Vector2.magnitude(vector);
        vector.x /= mag;
        vector.y /= mag;
    }
    static normalized(v1,v2) {
        if (v2 === undefined) {
            let mag = Vector2.magnitude(v1);
            return { x: v1.x / mag, y: v1.y / mag };
        } else {
            let mag = Vector2.magnitude(v1, v2);
            return { x: (v1.x - v2.x) / mag, y: (v1.y - v2.y) / mag };
        }
    }

    static dot(v1,v2) {
        return v1.x * v2.x + v1.y * v2.y;
    }
    static dotNormalized(v1,v2) {
        return Vector2.dot(v1,v2) / (Vector2.magnitude(v1) * Vector2.magnitude(v2));
    }

    static getAngle(vector) {
        let angle = Vector2.angleBetween(Vector2.up(), vector);
        if (Vector2.dotNormalized(Vector2.right(), vector) < 0) {
            angle = 360 - angle;
        }
        return angle;
    }

    static angleBetween(v1,v2) {
        let len1 = Vector2.magnitude(v1);
        let len2 = Vector2.magnitude(v2);

        if (len1 == 0 || len2 == 0) return 0;

        let value = Vector2.dot(v1, v2) / (len1 * len2);

        if (value < -1 || value > 1) return 0;

        return Math.radiansToDegrees(Math.acos(value));
    }

    static rotate(vector,degrees) {
        degrees = Math.degreesToRadians(degrees);
        return {
            x: vector.x * Math.cos(degrees) - vector.y * Math.sin(degrees),
            y: vector.x * Math.sin(degrees) + vector.y * Math.cos(degrees)
        };
    }
    static getDirection(degrees) {
        return {
            x: Math.cos(Math.degreesToRadians(degrees - 90)),
            y: Math.sin(Math.degreesToRadians(degrees - 90))
        };
    }

    static lerp(v1,v2,delta) {
        return {
            x: Math.lerp(v1.x, v2.x, delta),
            y: Math.lerp(v1.y, v2.y, delta)
        };
    }

    static moveTowards(current,target,maxDelta) {
        return {
            x: Math.moveTowards(current.x, target.x, maxDelta),
            y: Math.moveTowards(current.y, target.y, maxDelta)
        };
    }

    static invert(vector) {
        vector.x = -vector.x;
        vector.y = -vector.y;
        return;
    }
    static inverted(vector) {
        return {
            x: -vector.x,
            y: -vector.y
        };
    }

    static simplify(vector) {
        return {
            x: vector.x,
            y: vector.y
        };
    }

    static toString(vector) {
        return "(" +
            vector.x + ", " +
            vector.y +
        ")";
    }
}

export class Vector3 {
    static zero() {
        return { x: 0, y: 0, z: 0 };
    }
    static up() {
        return { x: 0, y: -1, z: 0 };
    }
    static right() {
        return { x: 1, y: 0, z: 0 };
    }
    static down() {
        return { x: 0, y: 1, z: 0 };
    }
    static left() {
        return { x: -1, y: 0, z: 0 };
    }
    static forward() {
        return { x: 0, y: 0, z: 1 };
    }
    static back() {
        return { x: 0, y: 0, z: -1 };
    }

    static add(v1,v2) {
        return {
            x: v1.x + v2.x,
            y: v1.y + v2.y,
            z: v1.z + (v2.z === undefined ? 0 : v2.z)
        };
    }

    static subtract(vector,fromVector) {
        return {
            x: fromVector.x - vector.x,
            y: fromVector.y - vector.y,
            z: fromVector.z - vector.z
        };
    }

    static multiply(vector,value) {
        return { 
            x: vector.x * value,
            y: vector.y * value,
            z: vector.z * value
        };
    }

    static divide(vector,value) {
        return {
            x: vector.x / value,
            y: vector.y / value,
            z: vector.z / value
        };
    }

    static __magnitude(x,y,z) {
        return Math.sqrt(x * x + y * y + z * z);
    }
    static magnitude(v1,v2) {
        return v2 === undefined ? 
            Vector3.__magnitude(v1.x, v1.y, v1.z) :
            Vector3.__magnitude(v1.x - v2.x, v1.y - v2.y, v1.z - v2.z);
    }

    static normalize(vector) {
        let mag = Vector3.magnitude(vector);
        vector.x /= mag;
        vector.y /= mag;
        vector.z /= mag;
    }
    static normalized(v1,v2) {
        if (v2 === undefined) {
            let mag = Vector3.magnitude(v1);
            return { x: v1.x / mag, y: v1.y / mag, z: v1.z / mag };
        } else {
            let mag = Vector3.magnitude(v1, v2);
            return { x: (v1.x - v2.x) / mag, y: (v1.y - v2.y) / mag, z: (v1.z - v2.z) / mag };
        }
    }

    static dot(v1,v2) {
        return v1.x * v2.x + v1.y * v2.y + v1.z * v2.z;
    }
    static dotNormalized(v1,v2) {
        return Vector3.dot(v1, v2) / (Vector3.magnitude(v1) * Vector3.magnitude(v2));
    }

    static normalizeAngles(vector) {
        vector.x = Vector3.normalizeAngle(vector.x);
        vector.y = Vector3.normalizeAngle(vector.y);
        vector.z = Vector3.normalizeAngle(vector.z);
    }
    static normalizeAngle(angle) {
        while (angle > 360)
            angle -= 360;
        while (angle < 0)
            angle += 360;
        return angle;
    }

    static cross(v1,v2) {
        return {
            x: (v1.y * v2.z) - (v1.z * v2.y),
            y: (v1.z * v2.x) - (v1.x * v2.z),
            z: (v1.x * v2.y) - (v1.y * v2.x)
        };
    }

    static radiansToDegrees(vector) {
        vector.x = Math.radiansToDegrees(vector.x);
        vector.y = Math.radiansToDegrees(vector.y);
        vector.z = Math.radiansToDegrees(vector.z);
        return vector;
    }
    static degreesToRadians(vector) {
        vector.x = Math.degreesToRadians(vector.x);
        vector.y = Math.degreesToRadians(vector.y);
        vector.z = Math.degreesToRadians(vector.z);
        return vector;
    }

    static getAngle(vector) {
        let angle = Vector3.angleBetween(Vector3.up(), vector);
        if (Vector3.dotNormalized(Vector3.right(), vector) < 0) {
            angle = 360 - angle;
        }
        return angle;
    }

    static angleBetween(v1,v2) {
        let len1 = Vector3.magnitude(v1);
        let len2 = Vector3.magnitude(v2);

        if (len1 == 0 || len2 == 0) return 0;

        let value = Vector3.dot(v1, v2) / (len1 * len2);

        if (value < -1 || value > 1) return 0;

        return Math.radiansToDegrees(Math.acos(value));
    }

    static rotate(vector,degrees) {
        Vector3.roll(vector, degrees);
    }
    static getDirection(degrees) {
        return {
            x: Math.cos(Math.degreesToRadians(degrees - 90)),
            y: Math.sin(Math.degreesToRadians(degrees - 90)),
            z: 0
        };
    }

    static pitch(vector,degrees) {
        degrees = Math.degreesToRadians(degrees);
        return {
            x: vector.x,
            y: vector.y * Math.cos(degrees) - vector.z * Math.sin(degrees),
            z: vector.y * Math.sin(degrees) + vector.z * Math.cos(degrees)
        };
    }
    static yaw(vector,degrees) {
        degrees = Math.degreesToRadians(degrees);
        return {
            x: vector.z * Math.sin(degrees) + vector.x * Math.cos(degrees),
            y: vector.y,
            z: vector.z * Math.cos(degrees) - vector.x * Math.sin(degrees)
        };
    }
    static roll(vector,degrees) {
        degrees = Math.degreesToRadians(degrees);
        return {
            x: vector.x * Math.cos(degrees) - vector.y * Math.sin(degrees),
            y: vector.x * Math.sin(degrees) + vector.y * Math.cos(degrees),
            z: vector.z
        };
    }

    static lerp(v1,v2,delta) {
        return {
            x: Math.lerp(v1.x, v2.x, delta),
            y: Math.lerp(v1.y, v2.y, delta),
            z: Math.lerp(v1.z, v2.z, delta)
        };
    }

    static moveTowards(current,target,maxDelta) {
        return {
            x: Math.moveTowards(current.x, target.x, maxDelta),
            y: Math.moveTowards(current.y, target.y, maxDelta),
            z: Math.moveTowards(current.z, target.z, maxDelta)
        };
    }

    static invert(vector) {
        vector.x = -vector.x;
        vector.y = -vector.y;
        if (vector.z !== undefined) {
            vector.z = -vector.z;
        }
        return vector;
    }
    static inverted(vector) {
        return {
            x: -vector.x,
            y: -vector.y,
            z: -vector.z
        };
    }

    static simplify(vector) {
        return {
            x: vector.x,
            y: vector.y,
            z: vector.z
        };
    }

    static getObjectForward(euler) {
        let cry = euler.x * Math.PI / 180.0;
        let crz = euler.y * Math.PI / 180.0;
        return {
            x: Math.sin(crz) * Math.cos(cry),
            y: -Math.sin(cry),
            z: Math.cos(crz) * Math.cos(cry)
        };
    }

    static toString(vector) {
        return "(" +
            vector.x + ", " +
            vector.y +
            (vector.z === undefined ? "" : ", " +  vector.z) +
        ")";
    }
}